import { createSelector } from '@reduxjs/toolkit';
import { GlobalState } from '../rootReducer';
import {
    LOAD_LIVE_CATALOGS_CONFIG_SUCCESS,
    LOAD_LIVE_CATALOGS_CONFIG_SUCCESS_ACTION,
    UPDATE_MESSAGING_PROVIDER,
    UPDATE_MESSAGING_PROVIDER_ACTION,
} from './actions';
import { PubSubConfig, PubSubProvider } from '@/types/PubSubProvider';

export interface State {
    algoliaApiKey: string;
    algoliaAppId: string;
    baseUrl: string;
    buildNumber: string;
    classicBaseUrl: string;
    curatorImgUrl: string;
    deployment: string;
    disableVendorPrefixes: boolean;
    globalNotificationClientType: string;
    googleApiKey: string;
    googlePageSpeedInsightsApiKey: string;
    helpScout: {
        appId: string;
        secret: string;
    };
    logRocketAppId: string;
    messagingProvider: PubSubProvider;
    messagingProviderKeyConfigs: {
        ably: PubSubConfig;
        pubnub: PubSubConfig;
    };
    partnersBaseUrl: string;
    pubNubNotificationsSubscribeKey: string;
    pubNubSubscribeKey: string;
    pushSubscriptionPublicKey: string;
    referralCandy: {
        houseIdForReferrals: number;
        id: number;
        secret: string;
    };
    sailthruCustomerId: string;
    segmentWriteKey: string;
    useSignifyd: boolean;
    wwwBaseUrl: string;
    zipMerchantKey: string;
}

/* reducer */
export const DEFAULT_STATE: State = {
    algoliaApiKey: '',
    algoliaAppId: '',
    baseUrl: '',
    buildNumber: '',
    classicBaseUrl: '',
    curatorImgUrl: '',
    deployment: '',
    disableVendorPrefixes: false,
    globalNotificationClientType: '',
    googleApiKey: '',
    googlePageSpeedInsightsApiKey: '',
    helpScout: {
        appId: '',
        secret: '',
    },
    logRocketAppId: '',
    messagingProvider: null,
    messagingProviderKeyConfigs: {
        ably: {
            publishKey: '',
            subscribeKey: '',
        },
        pubnub: {
            publishKey: '',
            subscribeKey: '',
        },
    },
    partnersBaseUrl: '',
    pubNubNotificationsSubscribeKey: '',
    pubNubSubscribeKey: '',
    pushSubscriptionPublicKey: '',
    referralCandy: {
        houseIdForReferrals: 0,
        id: 0,
        secret: '',
    },
    sailthruCustomerId: '',
    segmentWriteKey: '',
    useSignifyd: false,
    wwwBaseUrl: '',
    zipMerchantKey: '',
};

type Action = LOAD_LIVE_CATALOGS_CONFIG_SUCCESS_ACTION;

export default function reducer(state: State = DEFAULT_STATE, action: Action): State {
    switch (action?.type) {
        case LOAD_LIVE_CATALOGS_CONFIG_SUCCESS: {
            return {
                ...state,
                messagingProvider: action.payload.provider,
                messagingProviderKeyConfigs: {
                    ...state.messagingProviderKeyConfigs,
                    pubnub: action.payload.pubnub,
                },
            };
        }
        default:
            return state;
    }
}

/* SELECTORS */
const stateSelector = (state: GlobalState): State => state.config;

export const getAlgoliaApiKey = createSelector(stateSelector, (state) => state.algoliaApiKey);
export const getAlgoliaAppId = createSelector(stateSelector, (state) => state.algoliaAppId);
export const getBaseUrl = createSelector(stateSelector, (state) => state.baseUrl);
export const getBuildNumber = createSelector(stateSelector, (state) => state.buildNumber);
export const getClassicBaseUrl = createSelector(stateSelector, (state) => state.classicBaseUrl);
export const getCuratorBaseUrl = createSelector(stateSelector, (state) => state.curatorImgUrl);
export const getDeployment = createSelector(stateSelector, (state) => state.deployment);
export const getDisableVendorPrefixes = createSelector(stateSelector, (state) => state.disableVendorPrefixes);
export const getGlobalNotificationClientType = createSelector(
    stateSelector,
    (state) => state.globalNotificationClientType
);
export const getGoogleApiKey = createSelector(stateSelector, (state) => state.googleApiKey);
export const getGooglePageSpeedInsightsApiKey = createSelector(
    stateSelector,
    (state) => state.googlePageSpeedInsightsApiKey
);
export const getHelpScoutInformation = createSelector(stateSelector, (state) => state.helpScout);
export const getLogRocketAppId = createSelector(stateSelector, (state: State) => state.logRocketAppId);
export const getPartnersBaseUrl = createSelector(stateSelector, (state: State) => state.partnersBaseUrl);
export const getPubnubNotificationsSubscribeKey = createSelector(
    stateSelector,
    (state) => state.pubNubNotificationsSubscribeKey
);
export const getPubnubSubscribeKey = createSelector(stateSelector, (state) => state.pubNubSubscribeKey);
export const getPushSubscriptionPublicKey = createSelector(stateSelector, (state) => state.pushSubscriptionPublicKey);
export const getZipMerchantKey = createSelector(stateSelector, (state) => state.zipMerchantKey);
export const getReferralCandy = createSelector(stateSelector, (state) => state.referralCandy);
export const getSailthruCustomerId = createSelector(stateSelector, (state) => state.sailthruCustomerId);
export const getSegmentWriteKey = createSelector(stateSelector, (state) => state.segmentWriteKey);
export const getUseSignifyd = createSelector(stateSelector, (state) => state.useSignifyd);
export const getWWWBaseUrl = createSelector(stateSelector, (state) => state.wwwBaseUrl);

export const getMessagingProviderKeyConfigs = createSelector(
    stateSelector,
    (state: State) => state.messagingProviderKeyConfigs
);

export const getPubSubMessagingProvider = createSelector(stateSelector, (state: State) => state.messagingProvider);

/**
 * Returns the publishKey and subscribeKey for the current messagingProvider
 */
export const getPubSubMessagingProviderKeys = createSelector(
    [getMessagingProviderKeyConfigs, getPubSubMessagingProvider],
    (messagingProviderKeyConfigs, messagingProvider): PubSubConfig => {
        return (
            messagingProviderKeyConfigs[messagingProvider] || {
                publishKey: '',
                subscribeKey: '',
            }
        );
    }
);

/**
 * returns true if there is a valid subscribe key for the current pub/sub provider
 * useful for knowing if we can use the pub/sub rest apis
 */
export const hasPubSubConfig = createSelector(
    [getPubSubMessagingProviderKeys],
    (messagingProviderKeyConfig): boolean => {
        return Boolean(messagingProviderKeyConfig.subscribeKey);
    }
);

/* ACTION CREATORS */
export const updateMessagingProvider = (provider: PubSubProvider) => async (dispatch: Function) => {
    dispatch({
        payload: {
            provider,
        },
        type: UPDATE_MESSAGING_PROVIDER,
    } as UPDATE_MESSAGING_PROVIDER_ACTION);
};
